import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A namespaced view over the URL search params, for pages that host more than
 * one filtered list on a single route (tabs, side-by-side tables). Without a
 * prefix, two lists writing `page`/`search`/`subject` collide and switching tabs
 * bleeds stale params. With `usePrefixedSearchParams('teacher')`, keys become
 * `teacher.page`, `teacher.search`, … so each list owns its own slice of the URL.
 *
 * Pass an empty prefix for the page-level params (e.g. the active tab itself).
 * Writes default to `replace: true` so filter typing doesn't fill the history
 * stack — back returns to the origin, not through every keystroke.
 *
 * See docs/FeatureDesigns/back-navigation-context.md.
 */
export function usePrefixedSearchParams(prefix: string) {
    const [searchParams, setSearchParams] = useSearchParams();

    // Live view of the params so that multiple set() calls fired in the SAME
    // event handler compose instead of clobbering each other. React Router's
    // setSearchParams closes over the current-render `searchParams`, so two
    // synchronous calls both start from the same stale snapshot and the last
    // navigate() wins — silently dropping the first update (e.g. a filter change
    // immediately followed by a page reset). Seeding each call from a ref that
    // the previous call advanced makes the second build on the first. The ref is
    // reset to the committed params on every render.
    const liveRef = useRef(searchParams);
    liveRef.current = searchParams;

    const key = useCallback((k: string) => (prefix ? `${prefix}.${k}` : k), [prefix]);

    const get = useCallback(
        (k: string): string => searchParams.get(key(k)) ?? '',
        [searchParams, key],
    );

    /** Read a set of keys into a plain object; absent/empty keys are omitted. */
    const getAll = useCallback(
        (keys: readonly string[]): Record<string, string> => {
            const out: Record<string, string> = {};
            for (const k of keys) {
                const v = searchParams.get(key(k));
                if (v) out[k] = v;
            }
            return out;
        },
        [searchParams, key],
    );

    /** Set/clear keys under the prefix. Empty / null / undefined clears a key. */
    const set = useCallback(
        (updates: Record<string, string | number | null | undefined>, opts?: { replace?: boolean }) => {
            const p = new URLSearchParams(liveRef.current);
            for (const [k, v] of Object.entries(updates)) {
                const val = v == null ? '' : String(v);
                if (val) p.set(key(k), val); else p.delete(key(k));
            }
            // Advance the ref so a sibling set() in this same tick composes onto
            // this update rather than the stale committed snapshot.
            liveRef.current = p;
            setSearchParams(p, { replace: opts?.replace ?? true });
        },
        [setSearchParams, key],
    );

    return { get, getAll, set, searchParams };
}
